import { createLogger } from "logging";
import { AgendaItemDetailModel } from "models/AgendaItemDetailModel";
import { ConfirmDeleteTarget } from "models/ConfirmDeleteTarget";
import { ToggleType } from "models/ToggleType";
import { useApi } from "providers";
import { useMeeting } from "providers";
import { useToasts } from "providers";
import { useTranslations } from "providers";
import React, { PropsWithChildren, startTransition, useCallback, useEffect, useMemo, useState } from "react";

import { AgendaItemContext } from "./AgendaItemContext";
import { ConfirmDeleteAgendaItem } from "./components/ConfirmDeleteAgendaItem";

const logger = createLogger("AgendaItemProvider");

const isExpandedDefault = true;

export const AgendaItemProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const i18n = useTranslations();
  const toasts = useToasts();
  const { meeting, reloadMeeting } = useMeeting();
  const { agendaItemsApi } = useApi();

  const [confirmDeleteAgendaItem, setConfirmDeleteAgendaItem] = useState<ConfirmDeleteTarget | undefined>(undefined);
  const [selectAgendaItemId, setSelectAgendaItemId] = useState<number>(-1);

  const [expanded, setExpanded] = useState<ToggleType>({});
  const [deleting, setDeleting] = useState<ToggleType>({});
  const [updating, setUpdating] = useState<ToggleType>({});

  const [agendaItems, setAgendaItems] = useState<AgendaItemDetailModel[]>();

  const handleSetAgendaItems = useCallback(
    (itemsToSet: AgendaItemDetailModel[]) => {
      logger.debug("handleSetAgendaItems(): items:=", itemsToSet);
      setAgendaItems(itemsToSet);

      const newSelectedId = itemsToSet.find((x) => x.id === selectAgendaItemId)
        ? selectAgendaItemId
        : itemsToSet.length > 0
          ? itemsToSet[0].id
          : -1;

      logger.debug("handleSetAgendaItems(): selected:=", newSelectedId);
      setSelectAgendaItemId(newSelectedId);
    },
    [selectAgendaItemId]
  );

  useEffect(() => {
    startTransition(() => {
      handleSetAgendaItems(meeting?.agendaItems ?? []);
    });
  }, [meeting, handleSetAgendaItems]);

  const setAgendaItemExpanded = useCallback(
    (id: number, newState: boolean) => {
      setExpanded({ ...expanded, [id]: newState });
    },
    [expanded]
  );

  const isAgendaItemDeleting = useCallback(
    (id: number): boolean => {
      const flag = deleting[id];
      if (flag !== undefined) return flag;
      return false;
    },
    [deleting]
  );

  const setAgendaItemUpdating = useCallback((id: number, newState: boolean) => {
    setUpdating((prev) => ({ ...prev, [id]: newState }));
  }, []);

  const isAgendaItemUpdating = useCallback(
    (id: number): boolean => {
      const flag = updating[id];
      if (flag !== undefined) return flag;
      return false;
    },
    [updating]
  );

  const setAgendaItemDeleting = useCallback((id: number, newState: boolean) => {
    setDeleting((prev) => ({ ...prev, [id]: newState }));
  }, []);

  const isAgendaItemExpanded = useCallback(
    (id: number, defaultValue?: boolean): boolean => {
      const isExpanded = expanded[id];
      if (isExpanded !== undefined) return isExpanded;
      if (id == selectAgendaItemId) return true;
      if (defaultValue !== undefined) return defaultValue;
      return isExpandedDefault;
    },
    [expanded, selectAgendaItemId]
  );

  const toggleAgendaItemExpanded = useCallback(
    (id: number) => {
      const before = expanded[id];
      const after = before == undefined ? !isExpandedDefault : !before;
      logger.debug(`toggleAgendaItemExpanded(${id}): `, before, after);
      setAgendaItemExpanded(id, after);
    },
    [setAgendaItemExpanded, expanded]
  );

  const allowDrag = useMemo(() => {
    return meeting?.canAddAgendaItem == true;
  }, [meeting]);

  const onAgendaItemSortEnd = useCallback(
    async (oldIndex: number, newIndex: number) => {
      logger.debug(`onSortEnd(): `, oldIndex, newIndex);
      if (!agendaItems) throw new Error("Agendaitems null");

      const agendaItem = agendaItems[oldIndex];
      const id = agendaItem.id;
      const title = agendaItem.title;
      setAgendaItemUpdating(id, true);

      let previousItemId = 0;
      if (newIndex > 0) {
        const previousItem = agendaItems[newIndex - 1];
        previousItemId = previousItem.id;
      }

      try {
        await agendaItemsApi.reorder(id, previousItemId);
      } catch (error) {
        toasts.updateFailure(i18n.AgendaItem, title, error);
      } finally {
        setAgendaItemUpdating(id, false);
      }

      reloadMeeting();
    },
    [agendaItems, setAgendaItemUpdating, agendaItemsApi, i18n, toasts, reloadMeeting]
  );

  const contextValue = useMemo(
    () => ({
      agendaItems,
      setAgendaItems,
      confirmDeleteAgendaItem,
      setConfirmDeleteAgendaItem,
      selectAgendaItemId,
      setSelectAgendaItemId,
      isAgendaItemExpanded,
      toggleAgendaItemExpanded,
      setAgendaItemExpanded,
      setAgendaItemUpdating,
      isAgendaItemUpdating,
      setAgendaItemDeleting,
      isAgendaItemDeleting,
      onAgendaItemSortEnd,
      allowDrag,
    }),
    [
      agendaItems,
      setAgendaItems,
      confirmDeleteAgendaItem,
      setConfirmDeleteAgendaItem,
      selectAgendaItemId,
      setSelectAgendaItemId,
      isAgendaItemExpanded,
      toggleAgendaItemExpanded,
      setAgendaItemExpanded,
      setAgendaItemUpdating,
      isAgendaItemUpdating,
      setAgendaItemDeleting,
      isAgendaItemDeleting,
      onAgendaItemSortEnd,
      allowDrag,
    ]
  );

  return (
    <AgendaItemContext.Provider value={contextValue}>
      {children}
      <ConfirmDeleteAgendaItem />
    </AgendaItemContext.Provider>
  );
};
